import CloseIcon from "@mui/icons-material/Close"
import OpenInNewIcon from "@mui/icons-material/OpenInNew"
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material"
import type { GdeltClusterMemberArticle } from "./gdeltClusterMemberTypes"
import { useState } from "react"
import { Link as RouterLink } from "react-router"
import { extraColors } from "../../utils/theme"
import { buildBiothreatSignalDetailPath } from "./useBiothreatSignalById"
import { GdeltMemberListItem } from "./GdeltMemberListItem"
import {
  DrawerSectionLabel,
  DrawerVariantProvider,
  precomputedDrawerPaperSx,
  precomputedToggleGroupSx,
  summaryChipSx,
  type DrawerVariant,
} from "./precomputedDrawerStyles"
import {
  ImpactScoreBadge,
  DescriptionTypography,
} from "./PrecomputedSharedColumns"
import { formatGdeltActiveDateRange } from "./biothreatActiveDateRange"
import { getClusterMemberTitleSummary } from "./gdeltClusterMeta"
import type { PrecomputedBiothreatSignal } from "./types"
import {
  useGdeltClusterMembers,
  type GdeltClusterMembersResult,
  type GdeltClusterSourceSummary,
} from "./useGdeltClusterMembers"
import { GdeltClusterEvolutionSection } from "./GdeltClusterEvolutionSection"

const DRAWER_WIDTH = 520

const sourcePillSx = {
  borderColor: extraColors.translucentWhite,
  "& .MuiChip-label": {
    color: "rgba(255, 255, 255, 0.72)",
    fontWeight: 600,
  },
} as const

interface GdeltClusterDrawerProps {
  cluster: PrecomputedBiothreatSignal | null
  onClose: () => void
  /** "command" applies the v2 console treatment to the drawer. */
  variant?: DrawerVariant
}

export function GdeltClusterDrawer({
  cluster,
  onClose,
  variant = "default",
}: GdeltClusterDrawerProps) {
  return (
    <Drawer
      anchor="right"
      variant="persistent"
      open={cluster !== null}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: precomputedDrawerPaperSx(DRAWER_WIDTH, variant),
        },
      }}
    >
      {cluster && (
        <DrawerVariantProvider value={variant}>
          <GdeltClusterDrawerSummary
            cluster={cluster}
            onClose={onClose}
            detailHref={buildBiothreatSignalDetailPath(
              "gdelt",
              cluster.cluster_id
            )}
          />
          <Box sx={{ flex: 1, overflow: "auto" }}>
            <GdeltClusterMembersSection
              key={cluster.cluster_id}
              clusterId={cluster.cluster_id}
            />
          </Box>
        </DrawerVariantProvider>
      )}
    </Drawer>
  )
}

export function GdeltClusterMembersSection({
  clusterId,
}: {
  clusterId: string
}) {
  const [showUniqueOnly, setShowUniqueOnly] = useState(true)
  const membersData = useGdeltClusterMembers(clusterId, showUniqueOnly)

  return (
    <Stack spacing={2.5}>
      <GdeltClusterEvolutionSection clusterId={clusterId} />
      <Divider sx={{ borderColor: extraColors.translucent5 }} />
      <GdeltClusterMembersPanel
        {...membersData}
        onShowUniqueOnlyChange={setShowUniqueOnly}
      />
    </Stack>
  )
}

export function GdeltClusterDrawerSummary({
  cluster,
  onClose,
  detailHref,
}: {
  cluster: PrecomputedBiothreatSignal
  /** Omit on the standalone detail page (no drawer to close). */
  onClose?: () => void
  /** When set, the header shows an "Open full page" link (drawer only). */
  detailHref?: string
}) {
  return (
    <>
      <GdeltClusterDrawerHeader
        title={cluster.title || cluster.description || "Cluster details"}
        onClose={onClose}
        detailHref={detailHref}
      />
      <GdeltClusterSummaryChips cluster={cluster} />
      <GdeltClusterActivityDates cluster={cluster} />
      <GdeltClusterMemberTitleSummary cluster={cluster} />
      <GdeltClusterDescription cluster={cluster} />
      <Divider sx={{ borderColor: extraColors.translucent5, mb: 2 }} />
    </>
  )
}

function GdeltClusterDrawerHeader({
  title,
  onClose,
  detailHref,
}: {
  title: string
  onClose?: () => void
  detailHref?: string
}) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 1,
        mb: 2,
      }}
    >
      <Typography variant="h3Bold" sx={{ lineHeight: 1.3 }}>
        {title}
      </Typography>
      <Stack direction="row" spacing={0.5} sx={{ mt: "-4px", mr: "-4px" }}>
        {detailHref && (
          <IconButton
            component={RouterLink}
            to={detailHref}
            size="small"
            aria-label="Open full page"
            sx={{ color: extraColors.white }}
          >
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        )}
        {onClose && (
          <IconButton
            onClick={onClose}
            size="small"
            aria-label="Close drawer"
            sx={{ color: extraColors.white }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </Stack>
    </Box>
  )
}

function GdeltClusterSummaryChips({
  cluster,
}: {
  cluster: PrecomputedBiothreatSignal
}) {
  return (
    <Stack
      direction="row"
      spacing={1}
      useFlexGap
      sx={{ flexWrap: "wrap", alignItems: "center", mb: 1.5 }}
    >
      {cluster.impact_score != null && (
        <GdeltClusterImpactScore score={cluster.impact_score} />
      )}
      {cluster.threat_category && (
        <Chip
          size="small"
          variant="outlined"
          label={cluster.threat_category}
          sx={summaryChipSx}
        />
      )}
      {cluster.clean_disease_codes.map(code => (
        <GdeltClusterDiseaseCodeChip key={code} code={code} />
      ))}
      {cluster.resolved_location && (
        <Chip
          size="small"
          variant="outlined"
          label={
            cluster.gdelt_meta?.geo_level
              ? `${cluster.resolved_location} · ${cluster.gdelt_meta.geo_level}`
              : cluster.resolved_location
          }
          sx={summaryChipSx}
        />
      )}
    </Stack>
  )
}

function GdeltClusterDiseaseCodeChip({ code }: { code: string }) {
  return (
    <Chip
      size="small"
      variant="outlined"
      label={code.replace(/_/g, " ")}
      sx={summaryChipSx}
    />
  )
}

function GdeltClusterImpactScore({ score }: { score: number }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
      <ImpactScoreBadge score={score}>{score}</ImpactScoreBadge>
      <Typography variant="caption" sx={{ color: extraColors.translucent50 }}>
        Impact
      </Typography>
    </Box>
  )
}

function GdeltClusterMemberTitleSummary({
  cluster,
}: {
  cluster: PrecomputedBiothreatSignal
}) {
  const summary = getClusterMemberTitleSummary(cluster)
  if (!summary) {
    return null
  }

  return (
    <DescriptionTypography sx={{ mb: 2, WebkitLineClamp: "unset" }}>
      {summary}
    </DescriptionTypography>
  )
}

function GdeltClusterActivityDates({
  cluster,
}: {
  cluster: PrecomputedBiothreatSignal
}) {
  const range = formatGdeltActiveDateRange(cluster)
  const label = cluster.gdelt_meta?.first_reported_at ? "Active" : "Latest"

  return (
    <Typography
      variant="caption"
      sx={{ color: extraColors.translucent50, display: "block", mb: 2 }}
    >
      {`${label} ${range}`}
    </Typography>
  )
}

function GdeltClusterDescription({
  cluster,
}: {
  cluster: PrecomputedBiothreatSignal
}) {
  if (!cluster.description || cluster.description === cluster.title) {
    return null
  }

  return (
    <Typography
      variant="body2"
      sx={{
        color: extraColors.white,
        opacity: 0.85,
        mb: 2,
        lineHeight: 1.4,
      }}
    >
      {cluster.description}
    </Typography>
  )
}

function GdeltClusterMembersPanel({
  members,
  uniqueCount,
  totalCount,
  uniqueSourceCount,
  sources,
  isLoading,
  isError,
  showUniqueOnly,
  onShowUniqueOnlyChange,
}: GdeltClusterMembersResult & {
  onShowUniqueOnlyChange: (showUniqueOnly: boolean) => void
}) {
  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", pt: 4 }}>
        <CircularProgress size={24} />
      </Box>
    )
  }

  if (isError) {
    return (
      <Typography
        variant="body2"
        sx={{
          color: extraColors.status.redLight,
          textAlign: "center",
          pt: 4,
        }}
      >
        Failed to load cluster members.
      </Typography>
    )
  }

  if (members.length === 0) {
    return (
      <Typography
        variant="body2"
        sx={{ color: extraColors.hint, textAlign: "center", pt: 4 }}
      >
        No member articles found.
      </Typography>
    )
  }

  return (
    <GdeltClusterMembersContent
      members={members}
      uniqueCount={uniqueCount}
      totalCount={totalCount}
      uniqueSourceCount={uniqueSourceCount}
      sources={sources}
      showUniqueOnly={showUniqueOnly}
      onShowUniqueOnlyChange={onShowUniqueOnlyChange}
    />
  )
}

function GdeltClusterMembersContent({
  members,
  uniqueCount,
  totalCount,
  uniqueSourceCount,
  sources,
  showUniqueOnly,
  onShowUniqueOnlyChange,
}: Pick<
  GdeltClusterMembersResult,
  | "members"
  | "uniqueCount"
  | "totalCount"
  | "uniqueSourceCount"
  | "sources"
  | "showUniqueOnly"
> & {
  onShowUniqueOnlyChange: (showUniqueOnly: boolean) => void
}) {
  return (
    <Stack spacing={2.5}>
      <GdeltClusterCoverageStats
        showUniqueOnly={showUniqueOnly}
        uniqueCount={uniqueCount}
        totalCount={totalCount}
        uniqueSourceCount={uniqueSourceCount}
      />
      <GdeltClusterMembersList
        members={members}
        showUniqueOnly={showUniqueOnly}
        onShowUniqueOnlyChange={onShowUniqueOnlyChange}
      />
      <GdeltClusterSourcesList sources={sources} />
    </Stack>
  )
}

function GdeltClusterCoverageStats({
  showUniqueOnly,
  uniqueCount,
  totalCount,
  uniqueSourceCount,
}: {
  showUniqueOnly: boolean
  uniqueCount: number
  totalCount: number
  uniqueSourceCount: number
}) {
  if (!showUniqueOnly) {
    return (
      <Typography variant="small1Bold" sx={{ color: extraColors.white }}>
        {totalCount} {totalCount === 1 ? "article" : "articles"}
        <Box component="span" sx={{ color: extraColors.translucent50 }}>
          {" · "}
          {uniqueSourceCount} {uniqueSourceCount === 1 ? "source" : "sources"}
        </Box>
      </Typography>
    )
  }

  return (
    <Typography variant="small1Bold" sx={{ color: extraColors.white }}>
      {uniqueCount} unique {uniqueCount === 1 ? "headline" : "headlines"}
      <Box component="span" sx={{ color: extraColors.translucent50 }}>
        {" · "}
        {totalCount} {totalCount === 1 ? "article" : "articles"}
        {" · "}
        {uniqueSourceCount} {uniqueSourceCount === 1 ? "source" : "sources"}
      </Box>
    </Typography>
  )
}

function GdeltClusterMembersList({
  members,
  showUniqueOnly,
  onShowUniqueOnlyChange,
}: {
  members: GdeltClusterMemberArticle[]
  showUniqueOnly: boolean
  onShowUniqueOnlyChange: (showUniqueOnly: boolean) => void
}) {
  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          mb: 1,
        }}
      >
        <DrawerSectionLabel variant="caption" sx={{ mb: 0 }}>
          {showUniqueOnly ? "Unique Members" : "Members"}
        </DrawerSectionLabel>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={showUniqueOnly ? "unique" : "all"}
          onChange={(_event, value: "unique" | "all" | null) => {
            if (value != null) {
              onShowUniqueOnlyChange(value === "unique")
            }
          }}
          aria-label="Member view"
          sx={precomputedToggleGroupSx}
        >
          <ToggleButton value="unique">Unique</ToggleButton>
          <ToggleButton value="all">All</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <Stack spacing={1}>
        {members.map(member => (
          <GdeltClusterMemberRow key={member.raw_gkg_id} member={member} />
        ))}
      </Stack>
    </Box>
  )
}

function GdeltClusterMemberRow({
  member,
}: {
  member: GdeltClusterMemberArticle
}) {
  return (
    <GdeltMemberListItem
      title={member.title ?? ""}
      sourceName={member.source_name}
      publishTime={member.publish_time}
      url={member.url}
    />
  )
}

function GdeltClusterSourcesList({
  sources,
}: {
  sources: GdeltClusterSourceSummary[]
}) {
  if (sources.length === 0) return null

  return (
    <Box>
      <DrawerSectionLabel variant="caption" sx={{ mb: 1 }}>
        Sources
      </DrawerSectionLabel>
      <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
        {sources.map(source => (
          <SourceChip key={source.name} source={source} />
        ))}
      </Stack>
    </Box>
  )
}

function SourceChip({ source }: { source: GdeltClusterSourceSummary }) {
  const { name, count, homepage } = source
  const label = `${name} · ${count}`
  if (homepage) {
    return (
      <Chip
        size="small"
        variant="outlined"
        clickable
        component="a"
        href={homepage}
        target="_blank"
        rel="noopener noreferrer"
        label={label}
        sx={sourcePillSx}
      />
    )
  }
  return (
    <Chip size="small" variant="outlined" label={label} sx={sourcePillSx} />
  )
}
